import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup } from "@mui/material";
import { useCallback, useEffect, useReducer, useState } from "react";
import api, { RetailerDto, RetailerProductDto, RetailerProductUpdateDto } from "../../../api";
import { TokensControl } from "../../controls";
import { createStateReducer, notif } from "../../utils/util";
import { ProductList } from "./ProductList";
import { ProductAutocomplete } from "./ProductAutocomplete";

export interface RetailerProductDialogAction {
  open: boolean;
  retailer?: RetailerDto;
  productId?: string;
}

export interface RetailerProductDialogProps {
  action: RetailerProductDialogAction;
  onSubmit?: (dto: RetailerProductDto | null, action: "closed" | "added" | "updated") => void;
}

type StateAction = "none" | "add" | "edit";

const getRetailerProduct = async (retailerId?: string, productId?: string): Promise<RetailerProductDto | undefined> => {
  if (retailerId && productId) {
    try {
      return await api.retailerProduct.get(retailerId, productId);
    } catch (err) {
      // DO NOTHING
    }
  }
  return undefined;
};

const toRetailerProductUpdateDto = (dto: RetailerProductDto): RetailerProductUpdateDto =>
  ({
    retailerId: dto.retailerId,
    productId: dto.productId,
    settings: dto.settings,
  } as any);

export const RetailerProductDialog = (props: RetailerProductDialogProps) => {
  const { action, onSubmit } = props;

  const reducer = createStateReducer<RetailerProductUpdateDto>();
  const [state, dispatch] = useReducer(reducer, null);
  const [productId, setProductId] = useState("");
  const [stateAction, setStateAction] = useState<StateAction>("none");

  const loadState = useCallback(async () => {
    if (action.open && action.retailer?.id) {
      // if current state is "none" then dialog is opening
      const opening = stateAction === "none";
      let dto: RetailerProductDto | undefined;
      if (opening) {
        // opening, load vendor from props
        dto = await getRetailerProduct(action.retailer.id, action.productId);
      } else {
        // already opened, load from selected vendor in state
        dto = await getRetailerProduct(action.retailer.id, productId);
      }
      if (dto) {
        dispatch({ state: toRetailerProductUpdateDto(dto), replace: true });
        setStateAction("edit");
      } else {
        if (opening) {
          dispatch({ state: { retailerId: action.retailer.id, productId: "", settings: {} as any }, replace: true });
        }
        setStateAction("add");
      }
    } else {
      setStateAction("none");
    }
  }, [action, productId]);

  const handleAccept = async () => {
    try {
      const dto = await api.retailerProduct.update(state as any);
      if (onSubmit) onSubmit(dto, stateAction === "add" ? "added" : "updated");
      setStateAction("none");
    } catch (err) {
      notif(err, "error");
    }
  };
  const handleClose = () => {
    if (onSubmit) onSubmit(null, "closed");
    setStateAction("none");
  };

  const onProductChanged = async (productId: string) => {
    dispatch({ state: { productId } });
    setProductId(productId);
  };

  useEffect(() => {
    if (action.open && productId) {
      loadState();
    }
  }, [productId]);

  useEffect(() => {}, [state]);

  useEffect(() => {
    if (action.open && action.retailer?.id) {
      setProductId(action.productId ?? "");
      loadState();
    } else {
      setStateAction("none");
      setProductId("");
    }
  }, [action]);

  return (
    <Dialog open={stateAction !== "none"} onClose={handleClose} maxWidth="xl">
      <DialogTitle>
        {stateAction.toUpperCase()} RETAILER '{action?.retailer?.name}' PRODUCT
      </DialogTitle>
      <DialogContent dividers>
        <FormGroup>
          <ProductAutocomplete productId={state?.productId ?? ""} onChange={onProductChanged} />
          <FormControlLabel label="Enabled" control={<Checkbox checked={state?.isEnabled ?? true} onChange={(e, c) => dispatch({ state: { isEnabled: c } })} />} />
          <Divider />
          <br />
          <FormControl component="fieldset">
            <FormGroup>
              <TokensControl tokens={state?.settings?.tokens ?? []} onChange={(tokens) => dispatch({ state: { settings: { tokens } as any } })} />
              <FormControlLabel label="Exclude" control={<Checkbox checked={state?.settings?.exclude ?? false} onChange={(e, c) => dispatch({ state: { settings: { exclude: c } as any } })} />} />
            </FormGroup>
          </FormControl>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAccept}>Accept</Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
